import React from 'react';

import { twMerge } from 'tailwind-merge';

import Typography from 'components-new/typography/typography';
import type { IconProps } from 'components-new/icon/icon';
import Icon from 'components-new/icon/icon';

interface TextButtonProps {
  label: string;
  variant: 'primary' | 'secondary' | 'destructive';
  className?: string;
  labelClassName?: string;
  leftIconProps?: IconProps;
  rightIconProps?: IconProps;
  disabled?: boolean;
  onClick?: () => void;
}

const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  (
    {
      label,
      variant,
      disabled,
      onClick,
      className,
      labelClassName,
      leftIconProps,
      rightIconProps,
      ...props
    },
    ref,
  ) => {
    const buttonClassName = twMerge(
      'px-1.5 py-0.5 rounded-md',
      'group flex flex-row items-center relative shrink-0',
      'transition ease-in-out duration-150',
      'border border-transparent focus:outline active:outline-0 active:border-transparent',
      !disabled &&
        twMerge(
          variant === 'primary' &&
            'focus:border-primary-300 focus:outline-[1.5px] focus:outline-primary-200',
          variant === 'secondary' &&
            'focus:border-primary-300 focus:outline-[1.5px] focus:outline-primary-200',
          variant === 'destructive' &&
            'focus:border-destructive-300 focus:outline-[1.5px] focus:outline-destructive-200',
        ),
      className,
    );

    const labelClassNames = twMerge(
      !disabled &&
        twMerge(
          variant === 'primary' &&
            'text-primary-400 group-hover:text-primary-500 group-active:text-primary-300',
          variant === 'secondary' &&
            'text-gray-500 group-hover:text-gray-600 group-active:text-gray-400',
          variant === 'destructive' &&
            'text-destructive-400 group-hover:text-destructive-500 group-active:text-destructive-300',
        ),
      disabled &&
        twMerge(
          variant === 'primary' && 'text-primary-200',
          variant === 'secondary' && 'text-gray-300',
          variant === 'destructive' && 'text-destructive-200',
        ),
      labelClassName,
    );

    const iconClassName = twMerge('mr-1 shrink-0', labelClassNames);

    return (
      <button
        ref={ref}
        type="button"
        className={buttonClassName}
        disabled={disabled}
        onClick={onClick}
        {...props}
      >
        {leftIconProps && (
          <Icon className={iconClassName} {...leftIconProps} size={20} />
        )}
        <Typography
          variant="ui-button-md"
          as="span"
          className={labelClassNames}
        >
          {label}
        </Typography>

        {rightIconProps && (
          <Icon className={iconClassName} {...rightIconProps} size={20} />
        )}
      </button>
    );
  },
);

TextButton.displayName = 'TextButton';

export default TextButton;
